import React, {
    useContext,
    useCallback,
    useEffect,
    useState,
    useRef,
} from 'react';

import { Trans } from '@lingui/macro';
import query from 'query-string';

import { CommonStore, commonStore, CatalogStore, FamilyStore } from 'stores';
import {
    ERRORS,
    PATHS,
    FLASH,
    URLS,
    TRIAL_DISCOUNTS,
    PLAN_DURATIONS,
    PLAN_TYPES,
    PAYMENT,
} from 'config';

import { goto } from './routes.js';

import Pictorials from 'components/Pictorial/pictorials';
import * as help from 'helpers';

const valid = require('card-validator');

/**
 * Hides the navigation
 */
export function useHiddenNavigation() {
    const common = useContext(CommonStore);

    useEffect(() => {
        common.setNavigation(false);

        return () => {
            common.setNavigation(true);
        };
    }, [common]);
}

export function useElementOnScreen() {
    const elementRef = useRef(null);
    const [isOnscreen, setIsOnscreen] = useState(false);

    const observeCallback = (entries) => {
        const [entry] = entries;
        setIsOnscreen(entry.isIntersecting);
    };

    useEffect(() => {
        const { current: container } = elementRef;
        if (!container || !('IntersectionObserver' in window)) return undefined;

        const observer = new IntersectionObserver(observeCallback);
        observer.observe(container);

        return () => observer.unobserve(container);
    }, [elementRef]);

    return { elementRef, isOnscreen };
}

export function useZendeskWidget() {
    const getWidget = () => document.querySelector('iframe[id="launcher"]');

    const setVisible = useCallback((visible) => {
        const el = getWidget();
        if (el) {
            const classes = el.classList;
            visible ? classes.add('visible') : classes.remove('visible');
        }
    }, []);

    const afterWidgetLoaded = useCallback((cb) => {
        const widgetObserver = new MutationObserver(() => {
            const el = getWidget();
            if (el) {
                cb();
                widgetObserver.disconnect();
                return;
            }
        });

        widgetObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }, []);

    useEffect(() => {
        const sciptId = 'ze-snippet';
        function loadWidget() {
            const js = document.createElement('script');
            js.id = sciptId;
            js.src = URLS.zendeskWidget;
            document.head.appendChild(js);
        }

        if (!document.getElementById(sciptId)) {
            loadWidget();
            if (!help.isMobile()) afterWidgetLoaded(() => setVisible(true));
        }

        return () => setVisible(false);
    }, [setVisible, afterWidgetLoaded]);

    return {
        setWidgetVisible: setVisible,
    };
}

export function useTimeout(callback, delay) {
    const savedCallback = useRef(callback);

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        if (delay < 0) return;

        const id = setTimeout(() => savedCallback.current(), delay);
        return () => clearTimeout(id);
    }, [delay]);
}

/**
 * Shows a login wall in the current page for
 * users to confirm their identity.
 */
export function useLoginWallEffect(error, setError, i18n) {
    const [confirmed, setConfirmed] = useState(false);
    const commonStore = useContext(CommonStore);
    const { user } = commonStore;

    const onLoginSuccess = () => {
        setConfirmed(true);
        setError('');
    };

    const onFacebookResponse = (res) => {
        if (res.id && user.fb_profile_id && res.id === user.fb_profile_id) {
            setConfirmed(true);
            setError('');
        } else {
            setError(i18n._(ERRORS.facebook_wrong_account));
        }
    };

    return {
        confirmed,
        setConfirmed,
        loginFormProps: {
            logoutOnError: false,
            history: commonStore.routeHistory,
            loginErrorMessage: i18n._(ERRORS.password_current_invalid),
            onFacebookResponse: onFacebookResponse,
            onSuccess: onLoginSuccess,
            downloadDataAfterLogin: false,
            emailAddress: user.email,
            showForgotLink: false,
            noPadding: true,
            showFooter: false,
        },
    };
}

/**
 * Use query parameters from URL.
 */
export const useQueryParams = () => {
    return query.parse(commonStore.routeLocation.search);
};

export const useHashParams = () => {
    return query.parse(commonStore.routeLocation.hash);
};

/**
 * Uses a credit card form.
 */
const expiryMask = [/\d/, /\d/, '/', /\d/, /\d/];

export const useCreditCard = () => {
    const initialData = {
        number: '',
        cvv: '',
        name: '',
        expiry: '',
        email: commonStore && commonStore.user && commonStore.user.email,
        emailError: '',
        ZIPCode: '',
    };
    const [creditCardImage, setCreditCardImage] = useState(
        help.str_to_card_image('basic'),
    );
    const [cardMask, setCardMask] = useState(help.generate_input_mask());
    const [cvvMask, setCvvMask] = useState(help.generate_input_mask([], 3));
    const [validity, setValidity] = useState({});
    const [data, setData] = useState(initialData);

    const onUpdateCardNumber = (e) => {
        const { value } = e.currentTarget;
        const validate = valid.number(value);
        const { card } = validate;

        setValidity(validate);

        if (card && card.type) {
            setCardMask(
                help.generate_input_mask(
                    card.gaps,
                    card.lengths.length === 1
                        ? card.lengths[0]
                        : card.lengths.slice(-1)[0],
                ),
            );
            setCreditCardImage(help.str_to_card_image(card.type));

            if (card.code) {
                setCvvMask(help.generate_input_mask([], card.code.size));
            }
        } else {
            setCvvMask(help.generate_input_mask([], 3));
            setCardMask(help.generate_input_mask());
            setCreditCardImage(help.str_to_card_image('basic'));
        }

        setData({
            ...data,
            number: value,
        });
    };

    const onUpdateCardData = (e) => {
        const { name, value } = e.currentTarget;

        let newData = { ...data };
        newData[name] = value;

        if (name === 'email') {
            help.str_validate_email(value)
                ? (newData.emailError = '')
                : (newData.emailError = ERRORS.email_not_valid);
        }

        setData(newData);
    };

    const onUpdateZIPCode = (e) => {
        const { value } = e.currentTarget;
        setData({
            ...data,
            ZIPCode: value,
        });
    };

    let canSubmit =
        !data.emailError &&
        validity.isValid &&
        help.is_expiry_valid(data.expiry) &&
        data.cvv.length === cvvMask.length &&
        data.name;

    if (commonStore.ZIPCodeIsNeeded) {
        // for special countries, we also require zipcode
        canSubmit = canSubmit && data.ZIPCode && data.ZIPCode.length;
    }

    return {
        reset: () => setData(initialData),
        data,
        cardMask,
        validity,
        cvvMask,
        expiryMask,
        credit_card_error:
            data.number.length >= cardMask.length && !validity.isValid
                ? ERRORS.credit_card_number_invalid
                : null,
        expiry_error:
            data.expiry.length === expiryMask.length &&
            !help.is_expiry_valid(data.expiry)
                ? ERRORS.credit_card_expiry_invalid
                : null,
        CARD_PLACEHOLDER: '0',
        creditCardImage,
        onUpdateCardNumber,
        onUpdateCardData,
        onUpdateZIPCode,
        canSubmit,
    };
};

/**
 * Move focus to the next field of credit card form
 * when the previous field is valid.
 * Focus orders: card number, then expiry info, then cvv number
 */
export const useCreditCardFieldFocus = (card, data) => {
    const expiryRef = useRef(null);
    const cvvRef = useRef(null);

    useEffect(() => {
        card.validity?.isValid &&
            expiryRef.current &&
            expiryRef.current.inputElement.focus();
    }, [card.validity]);

    useEffect(() => {
        help.is_expiry_valid(data.expiry) &&
            cvvRef.current &&
            cvvRef.current.inputElement.focus();
    }, [data.expiry]);

    return { expiryRef, cvvRef };
};

/**
 * Validate plan ids coming from URL. Used in two
 * steps forms when users needs to select
 * a plan before continuing.
 */
export const usePlanValidation = (plans, path) => {
    const search = query.parse(commonStore.routeLocation.search);
    const params = ['id', 'plan']; // possible query parameters names for sending plans (id is used in landing pages, plan in app redirects)

    const usedParameter = params.find((q) => typeof search[q] !== 'undefined');

    const plan = search[usedParameter]
        ? plans.find((p) => p.id === search[usedParameter])
        : null;

    useEffect(() => {
        if (!plan) {
            commonStore.flashModal(FLASH.plan_validation_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Plan not found</Trans>,
                content: (
                    <p>
                        <Trans>
                            Sorry, we couldn't find the plan you selected. This
                            could be just a temporary error. Try again by
                            closing this message and selecting one of the plans
                            you will find in this page.
                        </Trans>
                    </p>
                ),
            });

            commonStore.routeHistory.replace(path);
        }
    });

    return plan;
};

/**
 * Validate plan ids coming from URL. Used in two
 * steps forms when users needs to select
 * a plan before continuing.
 */
export const usePlanValidationNoError = (plans, path) => {
    const search = query.parse(commonStore.routeLocation.search);
    const params = ['id', 'plan']; // possible query parameters names for sending plans (id is used in landing pages, plan in app redirects)

    const usedParameter = params.find((q) => typeof search[q] !== 'undefined');

    const plan = usedParameter
        ? plans.find((p) => p.id === search[usedParameter])
        : null;

    useEffect(() => {
        if (!plan) {
            commonStore.routeHistory.replace(path);
        }
    });

    return plan;
};

/**
 * Make sure the user can use a coupon
 */
export const useGuardCouponRedeem = () => {
    const common = useContext(CommonStore);
    const { subscription } = common;

    useEffect(() => {
        if (subscription) {
            commonStore.flashModal(FLASH.gift_redeem_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Something went wrong</Trans>,
                content: (
                    <p key="cant-redeem-coupon">
                        <Trans>
                            Unfortunately you can't redeem a coupon while you
                            have an active subscription.
                        </Trans>
                    </p>
                ),
            });

            commonStore.routeHistory.replace(PATHS.dashboard);
        }
    }, [subscription]);

    return {
        subscription,
    };
};

/**
 * Make sure the user can redeem
 * a gift card.
 */
export const useGuardGiftRedeem = () => {
    const common = useContext(CommonStore);
    const { subscription } = common;

    help.useEffectOnlyOnce(() => {
        if (subscription) {
            commonStore.flashModal(FLASH.gift_redeem_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Something went wrong</Trans>,
                content: (
                    <React.Fragment key="cant-redeem-gift-card">
                        <p>
                            <Trans>
                                Unfortunately you can't redeem your gift card
                                while you have an active subscription.
                            </Trans>
                        </p>
                        <p>
                            <a
                                {...help.blankLinkAttributes()}
                                href={`${URLS.help.giftingRedeem}#h_ced1706c-c962-45b3-b906-c03e4069a34d`}
                            >
                                <Trans>Learn more</Trans>
                            </a>
                        </p>
                    </React.Fragment>
                ),
            });

            commonStore.routeHistory.replace(PATHS.dashboard);
        }
    });

    return {
        subscription,
    };
};

/**
 * Make sure user can actually cancel
 * their subscription.
 */
export const useGuardCancel = () => {
    const common = useContext(CommonStore);
    const { subscription } = common;

    useGuard(
        !subscription ||
            subscription.renewable.canceled ||
            subscription.is_family_member,
        PATHS.noAccessCancel,
    );

    return {
        subscription,
    };
};

/**
 * Make sure users can resubscribe.
 */
export const useGuardResubscribe = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const { subscription } = common;
    const plans = catalog.resubscribe();

    help.useGuard(
        !subscription || !subscription.can_resubscribe || !plans.length,
        PATHS.noAccessResubscribe,
    );

    return {
        plans,
        subscription,
    };
};

/**
 * Make sure users can upgrade.
 */
export const useGuardUpgrade = () => {
    const common = useContext(CommonStore);
    const catalogStore = useContext(CatalogStore);
    const { subscription } = common;
    const plans = catalogStore.upgrade();

    help.useGuard(
        !subscription || !subscription.is_upgradable || !plans.length,
        PATHS.noAccessUpgrade,
    );

    return {
        plans,
        subscription,
        catalogStore,
    };
};

/**
 * Track payment errors
 */
export const usePaymentErrors = () => {
    const show = useBoolean(false);
    const [response, setResponse] = useState(null);
    const [errors, setErrors] = useState(0);

    return {
        counter: errors,
        visible: show.value,
        response: response,
        setResponse: setResponse,
        show: () => {
            show.setTrue();
            setErrors(errors + 1);
        },
        hide: () => {
            show.setFalse();
        },
    };
};

/**
 * Make sure users can subscribe.
 */
export const useGuardGiftCheckoutSuccess = () => {
    const common = useContext(CommonStore);
    const query = help.useQueryParams();
    const { voucher } = query;
    const gift = common.userGifts.find((gift) => gift.gift_voucher === voucher);

    useEffect(() => {
        if (!gift) {
            commonStore.flashModal(FLASH.gift_checkout_success_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Something went wrong</Trans>,
                content: (
                    <p>
                        <Trans>
                            Sorry, we couldn't find your gift card. This could
                            be just a temporary error. If the payment was
                            successful, you should see the gift card in your
                            dashboard.
                        </Trans>
                    </p>
                ),
            });

            commonStore.routeHistory.replace(PATHS.dashboard);
        }
    });

    return {
        gift,
        voucher,
    };
};

function filter_plans_no_user(plans) {
    // excluding _billed_monthly plans as they are very specific to regions
    return plans.filter((p) => !p.is_yearly_monthly);
}

export const useGuardPlans = () => {
    const { subscription, user } = useContext(CommonStore);

    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();

    const plans = !user
        ? filter_plans_no_user(catalog.subscribe({ coupon: query.coupon }))
        : null;

    useEffect(() => {
        if (query.coupon && !catalog.isCouponValid(query.coupon)) {
            commonStore.flashModal(FLASH.plan_validation_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Invalid coupon</Trans>,
                content: (
                    <p key="cant-verify-coupon">
                        <Trans>
                            Unfortunately your coupon is either invalid or was
                            already used.
                        </Trans>
                    </p>
                ),
            });

            return commonStore.routeHistory.replace(PATHS.plans);
        }

        if (user) {
            if (subscription && subscription.is_upgradable)
                return help.goto(PATHS.upgrade);

            if (subscription && subscription.can_resubscribe)
                return help.goto(PATHS.resubscribe);

            help.goto(PATHS.subscribe, query);
        }
    });

    return {
        plans,
        query,
    };
};

/**
 * Make sure users can subscribe.
 */
export const useGuardSubscribe = () => {
    const { subscription } = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const plans = catalog.subscribe({ coupon: query.coupon });

    useGuard(subscription || !plans.length, PATHS.noAccessSubscribe);

    useEffect(() => {
        if (query.coupon && !catalog.isCouponValid(query.coupon)) {
            commonStore.flashModal(FLASH.plan_validation_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Invalid coupon</Trans>,
                content: (
                    <p key="invalid-coupon">
                        <Trans>
                            Unfortunately your coupon is either invalid or was
                            already used.
                        </Trans>
                    </p>
                ),
            });

            return commonStore.routeHistory.replace(PATHS.subscribe);
        }
    });

    return {
        plans,
        subscription,
        query,
    };
};

/**
 * Back to School
 */
export const useGuardBackToSchool = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    const plans = catalog.backToSchool();

    useEffect(() => {
        if (!help.is_campaign_active('back-to-school')) {
            commonStore.routeHistory.replace(PATHS.backToSchoolOver);
            return;
        }

        const errorModal = {
            pictorial: Pictorials.Error,
            modalTitle: <Trans>Something went wrong</Trans>,
            content: (
                <p key="cant-redeem-coupon">
                    <Trans>
                        Unfortunately, you are not eligible for this offer.
                    </Trans>
                </p>
            ),
        };

        if (!plans || !plans.length) {
            commonStore.flashModal(FLASH.common_dashboard_error, errorModal);
            commonStore.routeHistory.replace(PATHS.dashboard);
        } else if (subscription && (!plans || !plans.length)) {
            commonStore.flashModal(FLASH.common_dashboard_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Something went wrong</Trans>,
                content: (
                    <p key="cant-redeem-coupon">
                        <Trans>
                            You already have a Yousician subscription.
                            Unfortunately, you are not eligible for this offer.
                        </Trans>
                    </p>
                ),
            });

            commonStore.routeHistory.replace(PATHS.dashboard);
        }
    }, [plans, subscription]);

    return {
        plans,
        subscription,
        query,
    };
};

/**
 * Black Friday
 */
export const useGuardBlackFriday = (noFamily, userType) => {
    const catalog = useContext(CatalogStore);
    const { subscription } = useContext(CommonStore);
    const black_friday = catalog.blackFriday();
    const black_friday_no_fp = black_friday.filter((p) => !p.family);

    // we offer only P+ and P+F plans in BF so if some is not 'licensed' => plans are shown in JP/AR
    const licensedContent = black_friday.some((p) => p.licensed);

    // If the no family plans version is requested set plans to the filtered version unless its empty
    let plans =
        noFamily && black_friday_no_fp && black_friday_no_fp.length
            ? black_friday_no_fp
            : black_friday;

    // filter out non-family plans for 'family only' type of users
    if (licensedContent && userType === 'family') {
        plans = plans.filter((p) => p.family);
    }

    useEffect(() => {
        if (!help.is_campaign_active('black-friday')) {
            commonStore.routeHistory.replace(PATHS.blackFridayOver);
            return;
        }

        const errorModal = {
            pictorial: Pictorials.Error,
            modalTitle: <Trans>Something went wrong</Trans>,
            content: (
                <p key="cant-redeem-coupon">
                    <Trans>
                        Unfortunately, you are not eligible for this offer.
                    </Trans>
                </p>
            ),
        };

        if (!plans || !plans.length) {
            commonStore.flashModal(FLASH.common_dashboard_error, errorModal);
            commonStore.routeHistory.replace(PATHS.dashboard);
        } else if (subscription && (!plans || !plans.length)) {
            commonStore.flashModal(FLASH.common_dashboard_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Something went wrong</Trans>,
                content: (
                    <p key="cant-redeem-coupon">
                        <Trans>
                            You already have a Yousician subscription.
                            Unfortunately, you are not eligible for this offer.
                        </Trans>
                    </p>
                ),
            });

            commonStore.routeHistory.replace(PATHS.dashboard);
        }
    }, [plans, subscription]);

    return {
        plans,
        subscription,
        licensedContent,
    };
};

/**
 * April campaign (COVID-19 Edition)
 */
export const useGuardAprilDiscount = () => {
    const { subscription } = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const plans = catalog.aprilDiscount();

    useEffect(() => {
        if (!help.is_campaign_active('april-discount'))
            return commonStore.routeHistory.replace(PATHS.aprilDiscountOver);

        const isPlanAvailable = plans && plans.length > 0;

        if (!isPlanAvailable) {
            commonStore.flashModal(FLASH.common_dashboard_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Something went wrong</Trans>,
                content: subscription ? (
                    <p key="cant-apply-campaign">
                        <Trans>
                            You already have a Yousician subscription.
                            Unfortunately, you are not eligible for this offer.
                        </Trans>
                    </p>
                ) : (
                    <p key="cant-apply-campaign">
                        <Trans>
                            Unfortunately, you are not eligible for this offer.
                        </Trans>
                    </p>
                ),
            });

            return commonStore.routeHistory.replace(PATHS.dashboard);
        }
    }, [plans, subscription]);

    return {
        plans,
        subscription,
    };
};

/**
 * Yousician25
 */
export const useGuardYousician25 = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    const plans = catalog.yousician25();

    useEffect(() => {
        if (!help.is_campaign_active('yousician-25')) {
            commonStore.routeHistory.replace(PATHS.yousician25Over);
            return;
        }

        const errorModal = {
            pictorial: Pictorials.Error,
            modalTitle: <Trans>Something went wrong</Trans>,
            content: (
                <p key="cant-redeem-coupon">
                    Unfortunately, you are not eligible for this offer.
                </p>
            ),
        };

        if (!plans || !plans.length) {
            commonStore.flashModal(FLASH.common_dashboard_error, errorModal);
            commonStore.routeHistory.replace(PATHS.dashboard);
        } else if (subscription && (!plans || !plans.length)) {
            commonStore.flashModal(FLASH.common_dashboard_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Something went wrong</Trans>,
                content: (
                    <p key="cant-redeem-coupon">
                        <Trans>
                            You already have a Yousician subscription.
                            Unfortunately, you are not eligible for this offer.
                        </Trans>
                    </p>
                ),
            });

            commonStore.routeHistory.replace(PATHS.dashboard);
        }
    }, [plans, subscription]);

    return {
        plans,
        subscription,
        query,
    };
};

/**
 * New Year
 */
export const useGuardNewYear = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    const plans = catalog.newYear();

    useEffect(() => {
        if (!help.is_campaign_active('new-year')) {
            commonStore.routeHistory.replace(PATHS.newYearOver);
            return;
        }

        const errorModal = {
            pictorial: Pictorials.Error,
            modalTitle: <Trans>Something went wrong</Trans>,
            content: (
                <p key="cant-redeem-coupon">
                    <Trans>
                        Unfortunately, you are not eligible for this offer.
                    </Trans>
                </p>
            ),
        };

        if (!plans || !plans.length) {
            commonStore.flashModal(FLASH.common_dashboard_error, errorModal);
            commonStore.routeHistory.replace(PATHS.dashboard);
        } else if (subscription && (!plans || !plans.length)) {
            commonStore.flashModal(FLASH.common_dashboard_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>Something went wrong</Trans>,
                content: (
                    <p key="cant-redeem-coupon">
                        <Trans>
                            You already have a Yousician subscription.
                            Unfortunately, you are not eligible for this offer.
                        </Trans>
                    </p>
                ),
            });

            commonStore.routeHistory.replace(PATHS.dashboard);
        }
    }, [plans, subscription]);

    return {
        plans,
        subscription,
        query,
    };
};
/**
 * Winback Guard
 */
export const useGuardWinback = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    let plans = catalog.winback();

    // if the user has payments planned e.g. on a canceled yearly_monthly, we cannot apply this campaign coupon to it, in the middle of the period
    const userHasFuturePayments =
        subscription && subscription.billing && subscription.billing.next;

    useEffect(() => {
        if (!plans.length || userHasFuturePayments) {
            commonStore.routeHistory.replace(PATHS.noAccessWinback);
            return;
        }
    }, [plans, userHasFuturePayments]);

    return {
        plans,
        subscription,
        query,
    };
};

/**
 * Free Trial Offer Guard
 *
 * When users shouldn't see the free trial offer page again
 */
export const useGuardFreeTrialOffer = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    const plans = catalog.freeTrialOffer() || [];

    help.useGuard(!plans.length, PATHS.noAccessFreeTrialOffer);

    return {
        plans,
        subscription,
        query,
    };
};

/**
 * Free Trial Offer 7 Guard
 *
 * When users shouldn't see the free trial offer page again
 */
export const useGuardFreeTrialOffer7 = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    const plans = catalog.freeTrialOffer7() || [];

    help.useGuard(!plans.length, PATHS.noAccessFreeTrialOffer);

    return {
        plans,
        subscription,
        query,
    };
};

/**
 * Free Trial Offer 14 Guard
 *
 * When users shouldn't see the free trial offer page again
 */
export const useGuardFreeTrialOffer14 = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    const plans = catalog.freeTrialOffer14() || [];

    help.useGuard(!plans.length, PATHS.noAccessFreeTrialOffer);

    return {
        plans,
        subscription,
        query,
    };
};

export const useGuardWorldMusicDay = ({ discountPercentage }) => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    const plans = catalog.worldMusicDay({ discountPercentage }) || [];

    useEffect(() => {
        if (!help.is_campaign_active('world-music-day')) {
            commonStore.routeHistory.replace(PATHS.worldMusicDayOver);
            return;
        }
    });

    help.useGuard(!plans.length, PATHS.noAccessWorldMusicDay);

    return {
        plans,
        subscription,
        query,
    };
};
/**
 * Trials Guard
 */
export const useGuardTrials = (match) => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const query = help.useQueryParams();
    const { token } = match.params;
    const discount = TRIAL_DISCOUNTS[token];

    help.useGuard(!discount, PATHS.noAccessTrials);

    const { subscription } = common;
    const plans = discount ? catalog.trials(discount.percentage) : [];
    help.useGuard(!plans.length, PATHS.noAccessTrials);

    return {
        plans,
        subscription,
        query,
        discount,
    };
};

/**
 * Make sure users have access to the gifting campaign.
 */
export const useGuardGifting = (needsAccess = true) => {
    const common = useContext(CommonStore);
    const exclude = ['AR', 'JP'];
    let hasAccess = exclude.indexOf(common.userLocation.country) >= 0;
    const redirection = needsAccess ? PATHS.noAccessGifting : PATHS.gift;

    if (!needsAccess) {
        hasAccess = !hasAccess;
    }

    useEffect(() => {
        if (hasAccess) {
            commonStore.routeHistory.replace(redirection);
        }
    }, [hasAccess, redirection]);
};

/**
 * Make sure users see the correct plan in the banner on the dashboard.
 */
export const useGuardBanner = () => {
    const common = useContext(CommonStore);
    const catalog = useContext(CatalogStore);
    const { subscription } = common;
    const query = help.useQueryParams();
    const initialPlans = catalog
        .subscribe({ coupon: query.coupon })
        .filter((x) => x.id === 'yearly_licensed' || x.id === 'yearly_all')[0];

    const [plan, setPlan] = useState(initialPlans);

    useEffect(() => {
        if (subscription && !subscription.family) {
            if (subscription.is_upgradable && catalog.upgrade().length) {
                setPlan(
                    catalog
                        .upgrade()
                        .filter((x) =>
                            x.id.includes('yearly_licensed_family'),
                        )[0],
                );
            } else if (
                subscription.renewable &&
                subscription.renewable.canceled &&
                catalog.resubscribe().length
            ) {
                setPlan(
                    catalog
                        .resubscribe()
                        .filter((x) =>
                            x.id.includes('yearly_licensed_family'),
                        )[0],
                );
            }
        }
    }, [subscription, catalog, query.coupon]);

    return {
        plan,
        subscription,
    };
};

/**
 * Make sure users who can add family members get access to the form.
 */
export const useGuardFamilyAddMembers = () => {
    const common = useContext(CommonStore);
    const { subscription } = common;
    const familyStore = useContext(FamilyStore);
    const { isAdmin } = familyStore;
    useGuard(
        !(subscription && subscription.family && isAdmin),
        PATHS.dashboard,
    );
};

/**
 * Guard external PayPal payment
 * @param {object} history Browser history object
 */
export const useGuardPaypalPayment = (history) => {
    const commonStore = useContext(CommonStore);
    const { url, return_to } = help.useQueryParams();
    const redirectUrl = url || return_to;

    useEffect(() => {
        if (!redirectUrl) {
            history.replace(PATHS.dashboard);
            return;
        }

        const { currencyCode, brandCode } = query.parse(redirectUrl);

        const isInvalidPaypalUrl =
            !help.canUsePaypal(currencyCode) && brandCode === PAYMENT.PAYPAL;
        if (isInvalidPaypalUrl) {
            commonStore.flashModal(FLASH.unsupported_currency_error, {
                pictorial: Pictorials.Error,
                modalTitle: <Trans>PayPal payment unsupported</Trans>,
                content: (
                    <p key="unsupported-currency">
                        <Trans>
                            Unfortunately, PayPal payment method is not
                            supported for your currency. Please try another
                            payment method.
                        </Trans>
                    </p>
                ),
            });
            commonStore.routeHistory.replace(PATHS.dashboard);
            return;
        }

        window.location.href = redirectUrl;
    }, [commonStore, history, redirectUrl]);
};

export const useGuardPurchase = (duration, type) => {
    const common = useContext(CommonStore);
    const query = help.useQueryParams();
    const { subscription } = common;
    const { plans, redirectTo } = help.get_plans_and_redirect(
        subscription,
        query.coupon,
        type,
    );

    useEffect(() => {
        if (
            !PLAN_DURATIONS.includes(duration) ||
            !PLAN_TYPES.includes(type) ||
            !plans
        ) {
            commonStore.routeHistory.replace(PATHS.dashboard);
        } else {
            let filtered_plan = null;
            switch (type) {
                case 'licensed':
                    filtered_plan = plans.filter(
                        (p) =>
                            p.id.startsWith(`${duration}_licensed`) &&
                            p.licensed &&
                            !p.family,
                    );
                    break;
                case 'family':
                    filtered_plan = plans.filter(
                        (p) =>
                            p.id.startsWith(`${duration}_licensed_family`) &&
                            p.family,
                    );
                    break;
                case 'all':
                    filtered_plan = plans.filter(
                        (p) =>
                            p.id.startsWith(`${duration}_all`) &&
                            !p.licensed &&
                            !p.family &&
                            p.has_all_instruments,
                    );
                    break;
                case 'premium':
                    filtered_plan = plans.filter(
                        (p) =>
                            p.id.startsWith(`${duration}`) &&
                            !p.licensed &&
                            !p.family,
                    );
                    break;
                default:
                    filtered_plan = plans.filter(
                        (p) =>
                            p.id.startsWith(`${duration}`) &&
                            !p.licensed &&
                            !p.family,
                    );
            }
            if (!filtered_plan.length) {
                commonStore.routeHistory.replace(PATHS.dashboard);
            } else {
                goto(redirectTo, { id: filtered_plan[0].id, ...query });
            }
        }
    }, [duration, type, plans, redirectTo, query]);
};

/**
 * Generic guard hook. Validates the condition and
 * redirect the users to {location} if the
 * condition is false.
 */
export const useGuard = (condition, location) => {
    useEffect(() => {
        if (condition) {
            commonStore.routeHistory.replace(location);
        }
        // eslint-disable-next-line
    }, []);
};

/**
 * Hook for usage of boolean state variables
 */
export const useBoolean = (initial) => {
    const [value, setValue] = useState(initial);
    return {
        value,
        setValue,
        toggle: () => setValue((v) => !v),
        setTrue: () => setValue(true),
        setFalse: () => setValue(false),
    };
};

export const useEffectOnlyOnce = (func) => useEffect(func, []);
